import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { AUTH_EVENTS, onAuthEvent } from '@/utils/cmsApi';
import IdleWarningModal from '@/components/admin/IdleWarningModal';

const API_URL = window.location.origin;

// Inactivity timeout — must stay ≤ the server's CMS_SESSION_IDLE_TTL_HOURS so
// the frontend never keeps a user "signed in" longer than the backend allows.
// Server default = 6h; we set client to 6h too. Overridable via env at build.
const INACTIVITY_LIMIT_MS =
  Number(process.env.REACT_APP_CMS_IDLE_MINUTES || 360) * 60 * 1000;

// Lead time before the auto-logout at which we show the warning modal.
const IDLE_WARNING_LEAD_MS =
  Number(process.env.REACT_APP_CMS_IDLE_WARNING_MINUTES || 5) * 60 * 1000;

// Events that count as "user activity" for resetting the idle timer.
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];

const CMSAuthContext = createContext(null);

export const CMSAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [modulePermissions, setModulePermissions] = useState([]);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Inactivity watchdog. lastActivityRef is a timestamp updated by ANY user
  // input; a poller checks whether the gap has crossed the limit
  // and, if so, logs the admin out.
  const lastActivityRef = useRef(Date.now());
  const idleWatchdogRef = useRef(null);
  // Effective idle limit (may be overridden per-session via ?_idleSecs query).
  const idleLimitMsRef = useRef(INACTIVITY_LIMIT_MS);
  // idleWarning is state so a modal component can subscribe and render.
  const [idleWarning, setIdleWarning] = useState(false);

  useEffect(() => {
    // Check for stored token on mount
    const storedToken = localStorage.getItem('cms_token');
    const storedUser = localStorage.getItem('cms_user');
    const storedPermissions = localStorage.getItem('cms_permissions');
    const storedModulePermissions = localStorage.getItem('cms_module_permissions');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      setPermissions(JSON.parse(storedPermissions || '{}'));
      setModulePermissions(JSON.parse(storedModulePermissions || '[]'));
    }
    setLoading(false);
    
    // Listen for unauthorized events from cmsApi
    const unsubscribe = onAuthEvent(AUTH_EVENTS.UNAUTHORIZED, () => {
      console.log('CMSAuthContext: Received unauthorized event, clearing state');
      setToken(null);
      setUser(null);
      setPermissions(null);
      setModulePermissions([]);
    });
    
    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/cms/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Invalid credentials');
      }
      
      // Store in state and localStorage
      setToken(data.access_token);
      setUser(data.user);
      setPermissions(data.permissions);
      setModulePermissions(data.module_permissions || []);
      
      localStorage.setItem('cms_token', data.access_token);
      localStorage.setItem('cms_user', JSON.stringify(data.user));
      localStorage.setItem('cms_permissions', JSON.stringify(data.permissions));
      localStorage.setItem('cms_module_permissions', JSON.stringify(data.module_permissions || []));
      
      return data;
    } catch (error) {
      // Re-throw with a cleaner message if it's a network error
      if (error.message?.includes('body stream')) {
        throw new Error('Invalid credentials');
      }
      throw error;
    }
  };

  // ── Passwordless (email OTP) sign-in ──
  // Step 1: request an OTP email via Resend. Returns { sent, cooldown_seconds }.
  const requestOTP = async (email) => {
    const response = await fetch(`${API_URL}/api/cms/auth/request-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.detail || 'Could not send verification code.');
    }
    return data;
  };

  // Step 2: verify the 6-digit code and finish sign-in. Same response shape
  // as /api/cms/login so the rest of the context is unchanged.
  const verifyOTP = async (email, code) => {
    const response = await fetch(`${API_URL}/api/cms/auth/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.detail || 'Verification failed.');
    }
    setToken(data.access_token);
    setUser(data.user);
    setPermissions(data.permissions);
    setModulePermissions(data.module_permissions || []);
    localStorage.setItem('cms_token', data.access_token);
    localStorage.setItem('cms_user', JSON.stringify(data.user));
    localStorage.setItem('cms_permissions', JSON.stringify(data.permissions));
    localStorage.setItem('cms_module_permissions', JSON.stringify(data.module_permissions || []));
    return data;
  };

  const logout = async () => {
    try {
      if (token) {
        await fetch(`${API_URL}/api/cms/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
    } catch (e) {
      // Ignore logout errors
    }
    
    // Clear state and storage
    setToken(null);
    setUser(null);
    setPermissions(null);
    setModulePermissions([]);
    localStorage.removeItem('cms_token');
    localStorage.removeItem('cms_user');
    localStorage.removeItem('cms_permissions');
    localStorage.removeItem('cms_module_permissions');
  };

  const hasPermission = (entity, action) => {
    if (!permissions) return false;
    const entityPerms = permissions[entity];
    return entityPerms ? entityPerms[action] : false;
  };

  // Idle watchdog: while a token is active, reset a timestamp on user input
  // and poll every 15 seconds. Cross the warning threshold → surface the
  // "you'll be signed out soon" modal; cross the hard limit → force-logout.
  const markActive = useCallback(() => {
    lastActivityRef.current = Date.now();
    // Any activity while the warning is visible also dismisses it — the
    // explicit "Stay signed in" button is just a nicer affordance.
    setIdleWarning((w) => (w ? false : w));
  }, []);

  // Explicit "Stay signed in" — resets the timer and hides the modal.
  const staySignedIn = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIdleWarning(false);
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    // Reset the timer whenever the token first becomes present (fresh sign-in
    // or context re-hydrate from localStorage).
    lastActivityRef.current = Date.now();
    setIdleWarning(false);

    // Optional URL query overrides for smoke-testing this flow without waiting
    // hours. Only takes effect for admin routes and only shrinks the windows.
    let limitMs = INACTIVITY_LIMIT_MS;
    let leadMs = IDLE_WARNING_LEAD_MS;
    try {
      const q = new URLSearchParams(window.location.search);
      const sLimit = Number(q.get('_idleSecs'));
      const sLead = Number(q.get('_warnSecs'));
      if (sLimit > 0 && sLimit * 1000 < limitMs) limitMs = sLimit * 1000;
      if (sLead > 0 && sLead * 1000 < limitMs) leadMs = sLead * 1000;
    } catch { /* ignore */ }

    // Keep the modal's deadline accessor in sync with the (possibly-overridden)
    // limit for this session.
    idleLimitMsRef.current = limitMs;

    ACTIVITY_EVENTS.forEach((evt) => {
      window.addEventListener(evt, markActive, { passive: true });
    });

    idleWatchdogRef.current = setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs >= limitMs) {
        // Persist a flag so the login screen can display an inactivity notice.
        try { sessionStorage.setItem('cms_last_logout_reason', 'idle'); } catch { /* ignore */ }
        logout();
        // Hard redirect ensures we leave any protected route we were sitting on.
        if (window.location.pathname.startsWith('/admin')) {
          window.location.replace('/admin/login');
        }
      } else if (idleMs >= limitMs - leadMs) {
        // Enter the warning zone — the modal will show a live countdown.
        setIdleWarning((w) => (w ? w : true));
      }
    }, 5 * 1000); // check every 5s so warning + countdown feel punctual

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, markActive));
      if (idleWatchdogRef.current) {
        clearInterval(idleWatchdogRef.current);
        idleWatchdogRef.current = null;
      }
    };
  }, [token]);

  /**
   * Check if user has access to a specific module
   * Super admin always has access to all modules
   */
  const hasModuleAccess = (moduleId) => {
    if (user?.role === 'super_admin') return true;
    return modulePermissions.includes(moduleId);
  };

  const canApprove = () => {
    return user?.role === 'super_admin' || user?.role === 'content_approver';
  };

  const isAdmin = () => {
    return user?.role === 'super_admin';
  };

  const value = {
    user,
    token,
    permissions,
    modulePermissions,
    loading,
    login,
    requestOTP,
    verifyOTP,
    logout,
    hasPermission,
    hasModuleAccess,
    canApprove,
    isAdmin,
    isAuthenticated: !!token,
    // Idle-warning state + action for the modal component to consume.
    idleWarning,
    staySignedIn,
    idleDeadlineMs: INACTIVITY_LIMIT_MS,
    getIdleDeadline: () => lastActivityRef.current + idleLimitMsRef.current,
  };

  return (
    <CMSAuthContext.Provider value={value}>
      {children}
      <IdleWarningModal />
    </CMSAuthContext.Provider>
  );
};

export const useCMSAuth = () => {
  const context = useContext(CMSAuthContext);
  if (!context) {
    throw new Error('useCMSAuth must be used within CMSAuthProvider');
  }
  return context;
};

export default CMSAuthContext;
